import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import gtu from '../../images/portfolio/gtu.jpg';
import { formatImageUrl, stripHtml } from '../../utils/security';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const HomePortfolioSlider = () => {
    const { t, i18n } = useTranslation();
    const { lang: urlLang } = useParams();
    const currentLang = urlLang || i18n.language || 'en';
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const trackRef = useRef(null);

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}portfolios.php?lang=${currentLang}`);
            const data = await response.json();
            if (Array.isArray(data)) {
                const formatted = data
                    .filter(p => p && p.title && (p.slug || p.id))
                    .map(p => {
                        const projectSlug = p.slug && p.slug !== 'undefined' ? p.slug : (p.id ? String(p.id) : '');
                        return {
                            id: p.id,
                            key: projectSlug,
                            slug: projectSlug,
                            category: p.category_name || p.category || 'PROJECT',
                            title: stripHtml(p.title),
                            summary: stripHtml(p.summary || ''),
                            image: formatImageUrl(p.image, gtu)
                        };
                    })
                    .filter(p => p.slug && p.slug !== 'undefined');
                setProjects(formatted);
            }
        } catch (error) {
            console.error('Error fetching portfolios for homepage:', error);
        }
        setLoading(false);
    }, [currentLang]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // Manual slide scroll
    const scroll = (dir) => {
        const track = trackRef.current;
        if (!track) return;
        const card = track.querySelector('.hp-slide');
        const amount = (card ? card.offsetWidth + 24 : 360) * dir;
        track.scrollBy({ left: amount, behavior: 'smooth' });
    };

    // Scroll to specific index
    const scrollToIndex = (idx) => {
        const track = trackRef.current;
        if (!track) return;
        const slides = track.querySelectorAll('.hp-slide');
        if (slides[idx]) {
            slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            setActiveIndex(idx);
        }
    };

    // Update active index on scroll
    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const handleScroll = () => {
            const card = track.querySelector('.hp-slide');
            if (!card) return;
            const cardWidth = card.offsetWidth + 24;
            const currentIdx = Math.round(track.scrollLeft / cardWidth);
            setActiveIndex(Math.min(currentIdx, Math.max(0, projects.length - 1)));
        };
        track.addEventListener('scroll', handleScroll, { passive: true });
        return () => track.removeEventListener('scroll', handleScroll);
    }, [projects.length]);

    // Autoplay slider loop
    useEffect(() => {
        if (loading || projects.length <= 1 || isPaused) return;

        const interval = setInterval(() => {
            const track = trackRef.current;
            if (!track) return;

            const maxScroll = track.scrollWidth - track.clientWidth;
            const card = track.querySelector('.hp-slide');
            const step = card ? card.offsetWidth + 24 : 360;

            if (track.scrollLeft >= maxScroll - 15) {
                // Smooth reset back to start
                track.scrollTo({ left: 0, behavior: 'smooth' });
                setActiveIndex(0);
            } else {
                track.scrollBy({ left: step, behavior: 'smooth' });
            }
        }, 3800);

        return () => clearInterval(interval);
    }, [loading, projects.length, isPaused]);

    return (
        <section 
            className="hp-portfolio-section" 
            id="portfolio"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div className="container">
                <div className="hp-portfolio-head">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="hp-portfolio-sub">
                                {t('home.portfolio.sub', 'ჩვენი ნამუშევრები')}
                            </span>
                            <span 
                                className={`hp-autoplay-badge ${isPaused ? 'paused' : 'playing'}`}
                                title={isPaused ? 'დაპაუზებულია მაუსის მიტანის გამო' : 'ავტო-სლაიდერი აქტიურია'}
                            >
                                <span className="hp-pulse-dot" />
                                {isPaused ? 'Paused' : 'Auto'}
                            </span>
                        </div>
                        <h2 className="hp-portfolio-title">
                            {t('home.portfolio.title', 'გამორჩეული პროექტები')}
                        </h2>
                        <p className="hp-portfolio-desc">
                            {t('home.portfolio.desc', 'გაეცანით ჩვენს მიერ განხორციელებულ წარმატებულ ციფრულ პროექტებსა და ინოვაციურ გადაწყვეტილებებს.')}
                        </p>
                    </div>

                    <div className="hp-portfolio-nav">
                        {!loading && projects.length > 0 && (
                            <>
                                <button 
                                    className="hp-arrow hp-arrow-prev" 
                                    onClick={() => scroll(-1)} 
                                    aria-label="Previous projects"
                                    title="წინა"
                                >
                                    <i className="fas fa-arrow-left" />
                                </button>
                                <button 
                                    className="hp-arrow hp-arrow-next" 
                                    onClick={() => scroll(1)} 
                                    aria-label="Next projects"
                                    title="შემდეგი"
                                >
                                    <i className="fas fa-arrow-right" />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="hp-portfolio-slider" ref={trackRef}>
                    {loading ? (
                        <>
                            {[0, 1, 2].map(i => (
                                <div className="hp-slide hp-slide-skeleton" key={i}>
                                    <div className="hp-card">
                                        <div className="hp-card-img-hp" />
                                        <div className="hp-card-line hp-card-line-long" />
                                        <div className="hp-card-line hp-card-line-short" />
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : projects.length === 0 ? (
                        <p className="text-white-50 text-center py-5 w-100">
                            {t('home.portfolio.empty', 'პროექტები არ არის დამატებული.')}
                        </p>
                    ) : (
                        projects.map((project, index) => {
                            const projectSlug = project.slug || project.key || project.id;
                            return (
                                <motion.div
                                    key={project.id || projectSlug || index}
                                    className="hp-slide"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3) }}
                                >
                                    <Link 
                                        to={`/${currentLang}/portfolio/${projectSlug}`} 
                                        className="hp-card"
                                        title={project.title}
                                    >
                                        <div className="hp-card-img">
                                            <img 
                                                src={project.image} 
                                                alt={project.title} 
                                                title={project.title}
                                                loading="lazy" 
                                                onError={(e) => { e.currentTarget.src = gtu; }}
                                            />
                                            <div className="hp-card-overlay">
                                                <span className="hp-view">
                                                    {t('portfolio.viewDetails', 'დეტალურად')}
                                                    <i className="fas fa-arrow-right ms-2" />
                                                </span>
                                            </div>
                                        </div>
                                        <div className="hp-card-meta">
                                            <span className="hp-card-category">
                                                {project.category}
                                            </span>
                                            <h3 className="hp-card-title">{project.title}</h3>
                                            {project.summary && (
                                                <p className="hp-card-summary text-truncate-2">
                                                    {project.summary}
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })
                    )}
                </div>

                {/* Slider progress dots */}
                {!loading && projects.length > 1 && (
                    <div className="hp-dots-container">
                        {projects.slice(0, Math.min(projects.length, 8)).map((_, idx) => (
                            <button
                                key={idx}
                                className={`hp-dot ${activeIndex === idx ? 'active' : ''}`}
                                onClick={() => scrollToIndex(idx)}
                                aria-label={`Go to slide ${idx + 1}`}
                                title={`Slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}

                <div className="hp-portfolio-footer text-center mt-5">
                    <Link to={`/${currentLang}/portfolio`} className="hp-view-all" title={t('home.portfolio.viewAll', 'ყველა პროექტი')}>
                        {t('home.portfolio.viewAll', 'ყველა პროექტი')}
                        <i className="fas fa-arrow-right ms-2" />
                    </Link>
                </div>
            </div>

            <style>{`
                .hp-portfolio-section {
                    padding: 90px 0 110px;
                    background: linear-gradient(180deg, transparent 0%, rgba(108, 92, 231, 0.04) 50%, transparent 100%);
                    position: relative;
                }
                .hp-portfolio-head {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 30px;
                    margin-bottom: 46px;
                    flex-wrap: wrap;
                }
                .hp-portfolio-sub {
                    color: #6c5ce7;
                    font-weight: 700;
                    font-size: 13px;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                }
                .hp-autoplay-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 2px 8px;
                    border-radius: 20px;
                    background: rgba(108, 92, 231, 0.15);
                    color: #a29bfe;
                    border: 1px solid rgba(108, 92, 231, 0.3);
                }
                .hp-autoplay-badge.paused {
                    background: rgba(255, 255, 255, 0.08);
                    color: rgba(255, 255, 255, 0.6);
                    border-color: rgba(255, 255, 255, 0.15);
                }
                .hp-pulse-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #10b981;
                    box-shadow: 0 0 8px #10b981;
                    animation: hp-pulse 2s infinite;
                }
                .hp-autoplay-badge.paused .hp-pulse-dot {
                    background: #f59e0b;
                    box-shadow: none;
                    animation: none;
                }
                @keyframes hp-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(0.8); }
                }
                .hp-portfolio-title {
                    color: #fff;
                    font-size: clamp(1.8rem, 3.6vw, 2.8rem);
                    font-weight: 800;
                    margin: 10px 0 10px;
                    line-height: 1.2;
                }
                .hp-portfolio-desc {
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 15px;
                    max-width: 560px;
                    margin: 0;
                    line-height: 1.6;
                }
                .hp-portfolio-nav {
                    display: flex;
                    gap: 12px;
                }
                .hp-arrow {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    background: rgba(255, 255, 255, 0.04);
                    color: #fff;
                    font-size: 16px;
                    cursor: pointer;
                    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                .hp-arrow:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(108, 92, 231, 0.3);
                }
                .hp-portfolio-slider {
                    display: flex;
                    gap: 24px;
                    overflow-x: auto;
                    scroll-snap-type: x mandatory;
                    scrollbar-width: none;
                    padding-bottom: 20px;
                    scroll-behavior: smooth;
                }
                .hp-portfolio-slider::-webkit-scrollbar {
                    display: none;
                }
                .hp-slide {
                    flex: 0 0 calc(33.333% - 16px);
                    min-width: 300px;
                    scroll-snap-align: start;
                }
                .hp-card {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    background: rgba(15, 20, 48, 0.7);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 18px;
                    text-decoration: none;
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    height: 100%;
                }
                .hp-card:hover {
                    background: rgba(22, 28, 65, 0.9);
                    border-color: rgba(108, 92, 231, 0.5);
                    transform: translateY(-8px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 30px rgba(108, 92, 231, 0.15);
                }
                .hp-card-img {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 16 / 10;
                    overflow: hidden;
                    border-radius: 14px;
                    margin-bottom: 16px;
                    background: #0d1226;
                }
                .hp-card-img img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .hp-card:hover .hp-card-img img {
                    transform: scale(1.08);
                }
                .hp-card-overlay {
                    position: absolute;
                    inset: 0;
                    background: rgba(15, 20, 48, 0.6);
                    backdrop-filter: blur(2px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                .hp-card:hover .hp-card-overlay {
                    opacity: 1;
                }
                .hp-view {
                    color: #fff;
                    font-weight: 600;
                    font-size: 13px;
                    text-transform: uppercase;
                    letter-spacing: 1.5px;
                    border: 1px solid rgba(255, 255, 255, 0.6);
                    background: rgba(108, 92, 231, 0.3);
                    padding: 9px 22px;
                    border-radius: 50px;
                    display: inline-flex;
                    align-items: center;
                    transition: all 0.2s;
                }
                .hp-card-meta {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                }
                .hp-card-category {
                    display: inline-block;
                    font-size: 11px;
                    text-transform: uppercase;
                    color: #a29bfe;
                    letter-spacing: 1.2px;
                    font-weight: 700;
                    margin-bottom: 6px;
                }
                .hp-card-title {
                    color: #fff;
                    font-size: 1.15rem;
                    font-weight: 700;
                    line-height: 1.35;
                    margin: 0 0 8px;
                }
                .hp-card-summary {
                    color: rgba(255, 255, 255, 0.55);
                    font-size: 13px;
                    line-height: 1.5;
                    margin: 0;
                }
                .text-truncate-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .hp-dots-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    margin-top: 25px;
                }
                .hp-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.2);
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    transition: all 0.3s;
                }
                .hp-dot.active {
                    width: 24px;
                    border-radius: 12px;
                    background: #6c5ce7;
                    box-shadow: 0 0 10px rgba(108, 92, 231, 0.5);
                }
                .hp-view-all {
                    display: inline-flex;
                    align-items: center;
                    color: #fff;
                    font-weight: 700;
                    text-decoration: none;
                    background: rgba(108, 92, 231, 0.15);
                    border: 1px solid rgba(108, 92, 231, 0.5);
                    padding: 14px 34px;
                    border-radius: 50px;
                    transition: all 0.3s;
                }
                .hp-view-all:hover {
                    background: #6c5ce7;
                    color: #fff;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(108, 92, 231, 0.4);
                }
                /* Skeleton */
                .hp-slide-skeleton .hp-card {
                    height: 100%;
                    pointer-events: none;
                }
                .hp-card-img-hp,
                .hp-card-line {
                    position: relative;
                    overflow: hidden;
                    background: rgba(255, 255, 255, 0.05);
                }
                .hp-card-img-hp::after,
                .hp-card-line::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    transform: translateX(-100%);
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
                    animation: hp-shimmer 1.4s infinite;
                }
                @keyframes hp-shimmer {
                    100% { transform: translateX(100%); }
                }
                .hp-card-img-hp {
                    aspect-ratio: 16 / 10;
                    border-radius: 14px;
                    margin-bottom: 18px;
                }
                .hp-card-line {
                    height: 14px;
                    border-radius: 6px;
                    margin-bottom: 12px;
                }
                .hp-card-line-long { width: 80%; }
                .hp-card-line-short { width: 55%; }
                @media (max-width: 991px) {
                    .hp-slide { flex: 0 0 calc(50% - 12px); min-width: 260px; }
                }
                @media (max-width: 640px) {
                    .hp-slide { flex: 0 0 86%; min-width: 240px; }
                }
            `}</style>
        </section>
    );
};

export default HomePortfolioSlider;